/**
 * Audit Log — Stage 9.
 *
 * Synthesised from append-only sources we already capture (DayClosingLog,
 * PaymentInitiationLog, BIAlertEvent, ProductStockOutLog) via
 * `/api/bi/audit/`. Filterable by source and freshness, with a tight
 * triage list.
 */
import { useMemo, useState } from 'react';
import {
  ScrollText,
  Wallet,
  Receipt,
  Bell,
  PackageX,
  Filter as FilterIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuditFeed } from '@/api/bi';
import type { AuditEvent } from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const SOURCE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  day_close: Wallet,
  payment: Receipt,
  alert: Bell,
  stockout: PackageX,
};

const SOURCE_TONE: Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  day_close: 'success',
  payment: 'info',
  alert: 'warning',
  stockout: 'danger',
};

const DAYS_OPTIONS = [1, 7, 14, 30] as const;

export function AuditPage() {
  const [days, setDays] = useState<number>(14);
  const [activeSources, setActiveSources] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useAuditFeed({
    days,
    sources: activeSources,
    limit: 200,
  });

  const sources = data?.sources ?? [];
  const events = data?.events ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) =>
        e.summary.toLowerCase().includes(q) ||
        (e.actor_name ?? '').toLowerCase().includes(q) ||
        (e.object_label ?? '').toLowerCase().includes(q),
    );
  }, [events, search]);

  function toggleSource(id: string) {
    setActiveSources((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Trust"
        title="Audit Log"
        description="A combined feed of every accountable thing happening inside Fasto — closings, payments, alerts, stock-outs."
      />

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FilterIcon className="h-4 w-4 text-muted-foreground" /> Filters
          </CardTitle>
          <Badge tone="neutral">{events.length} events</Badge>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Last</span>
            {DAYS_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={cn(
                  'press rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                  days === d
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {d === 1 ? '24h' : `${d}d`}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Sources</span>
            {sources.map((s) => {
              const active = activeSources.length === 0 || activeSources.includes(s.id);
              const Icon = SOURCE_ICON[s.id] ?? ScrollText;
              return (
                <button
                  key={s.id}
                  onClick={() => toggleSource(s.id)}
                  className={cn(
                    'press inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                    active && activeSources.includes(s.id)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70',
                  )}
                >
                  <Icon className="h-3 w-3" /> {s.label}
                </button>
              );
            })}
            {activeSources.length > 0 ? (
              <button
                onClick={() => setActiveSources([])}
                className="press text-[11px] text-muted-foreground underline-offset-4 hover:underline"
              >
                clear
              </button>
            ) : null}
          </div>
          <div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search actor, object or summary…"
              className="h-9 w-full rounded-md border border-border bg-surface px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Activity</CardTitle>
          <Badge tone="neutral">{filtered.length} shown</Badge>
        </CardHeader>
        <CardBody>
          {isError ? (
            <ErrorState description="Couldn't load audit feed." onRetry={() => void refetch()} />
          ) : isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-muted/40" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="Nothing matched"
              description="Widen the window or clear the source filters above."
            />
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((e, i) => (
                <EventRow key={`${e.source}-${e.object_id}-${e.ts}-${i}`} event={e} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function EventRow({ event }: { event: AuditEvent }) {
  const Icon = SOURCE_ICON[event.source] ?? ScrollText;
  const tone = SOURCE_TONE[event.source] ?? 'neutral';
  return (
    <li className="flex items-start gap-3 py-3">
      <span
        className={cn(
          'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          tone === 'success' && 'bg-success/10 text-success',
          tone === 'warning' && 'bg-warning/10 text-warning',
          tone === 'danger' && 'bg-danger/10 text-danger',
          tone === 'info' && 'bg-info/10 text-info',
          tone === 'neutral' && 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {event.actor_name ?? 'System'}
          </span>
          <span className="text-[11px] text-muted-foreground">{event.action.replace(/_/g, ' ')}</span>
          {event.object_label ? (
            <span className="truncate text-[11px] text-muted-foreground">
              · {event.object_label}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{event.summary}</p>
      </div>
      <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
        {fmt.ago(event.ts)}
      </span>
    </li>
  );
}
